
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Check, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useNotifications } from "@/hooks/use-notifications";

interface Notification {
  id: string;
  title: string;
  content: string;
  read: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
  type: string;
}

interface NotificationHistoryProps {
  notifications?: Notification[];
  isLoading?: boolean;
  limit?: number;
}

export const NotificationHistory = ({ 
  notifications: propNotifications, 
  isLoading: propIsLoading,
  limit
}: NotificationHistoryProps = {}) => {
  const { 
    notifications: hookNotifications, 
    isLoading: hookIsLoading, 
    markAsRead, 
    deleteNotification 
  } = useNotifications();
  
  // Use props if provided, otherwise use the hook values
  const notifications = propNotifications || hookNotifications;
  const isLoading = propIsLoading !== undefined ? propIsLoading : hookIsLoading;

  // Apply limit if specified
  const displayedNotifications = limit ? notifications?.slice(0, limit) : notifications;

  if (isLoading) {
    return <div className="p-4">Chargement des notifications...</div>;
  }

  if (!notifications?.length) {
    return <div className="p-4">Aucune notification</div>;
  }

  return (
    <ScrollArea className={limit ? "h-auto" : "h-[400px]"}>
      <div className="space-y-4 p-4">
        {displayedNotifications?.map((notification) => (
          <div
            key={notification.id}
            className={`flex items-start justify-between rounded-lg border p-4 ${
              notification.read ? "bg-muted" : "bg-card"
            }`}
          >
            <div className="space-y-1">
              <h4 className="font-medium">{notification.title}</h4>
              <p className="text-sm text-muted-foreground">
                {notification.content}
              </p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(notification.created_at), "PPp", { locale: fr })}
              </p>
            </div>
            <div className="flex gap-2">
              {!notification.read && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => markAsRead?.mutate(notification.id)}
                >
                  <Check className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deleteNotification?.mutate(notification.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};
